import React, { useState, useCallback } from 'react';
import { useWallet } from '../hooks/useWallet';
import { useNativeWallet } from '../hooks/useNativeWallet';

// ── Shared styles injected once ──────────────────────────────────────────────
const WALLET_STYLES = `
.wm-overlay {
  position: fixed; inset: 0;
  background: rgba(8,8,16,0.92);
  backdrop-filter: blur(16px);
  z-index: 2000;
  display: flex; align-items: center; justify-content: center;
  opacity: 0; pointer-events: none;
  transition: opacity 0.2s;
}
.wm-overlay.open { opacity: 1; pointer-events: all; }

.wm-panel {
  background: #0e0e1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 24px;
  width: 460px; max-width: 94vw;
  max-height: 90vh; overflow-y: auto;
  padding: 36px;
  transform: translateY(20px);
  transition: transform 0.22s;
  position: relative;
}
.wm-overlay.open .wm-panel { transform: translateY(0); }

.wm-close {
  position: absolute; top: 18px; right: 18px;
  background: transparent; border: none;
  color: rgba(255,255,255,0.3); font-size: 20px;
  cursor: pointer; line-height: 1;
  transition: color 0.15s;
}
.wm-close:hover { color: #fff; }

.wm-back {
  display: flex; align-items: center; gap: 6px;
  background: transparent; border: none;
  color: rgba(255,255,255,0.4); font-size: 12px;
  cursor: pointer; margin-bottom: 20px;
  font-family: 'DM Mono', monospace;
  letter-spacing: 0.05em;
  transition: color 0.15s;
  padding: 0;
}
.wm-back:hover { color: #fff; }

.wm-logo {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 28px;
}
.wm-logo-mark {
  width: 38px; height: 38px;
  border-radius: 10px;
  background: rgba(74,222,128,0.12);
  border: 1px solid rgba(74,222,128,0.25);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cabinet Grotesk', sans-serif;
  font-weight: 900; font-size: 20px; color: #4ade80;
}
.wm-logo-text {
  font-family: 'Cabinet Grotesk', sans-serif;
  font-weight: 800; font-size: 16px;
  letter-spacing: 0.1em; color: #fff;
}
.wm-logo-sub {
  font-family: 'DM Mono', monospace;
  font-size: 10px; color: rgba(255,255,255,0.3);
  letter-spacing: 0.1em; margin-top: 1px;
}

.wm-title {
  font-family: 'Cabinet Grotesk', sans-serif;
  font-size: 24px; font-weight: 800;
  color: #fff; margin-bottom: 6px;
  letter-spacing: -0.01em;
}
.wm-sub {
  font-size: 13px; color: rgba(255,255,255,0.45);
  line-height: 1.65; margin-bottom: 24px;
}

/* Option buttons */
.wm-option {
  display: flex; align-items: center; gap: 16px;
  width: 100%; text-align: left;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px; padding: 18px 20px;
  cursor: pointer; margin-bottom: 10px;
  transition: all 0.18s;
}
.wm-option:hover {
  border-color: rgba(74,222,128,0.3);
  background: rgba(74,222,128,0.06);
  transform: translateY(-1px);
}
.wm-option:last-child { margin-bottom: 0; }
.wm-option-icon {
  width: 44px; height: 44px; flex-shrink: 0;
  border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
}
.wm-option-icon.keplr { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); }
.wm-option-icon.native { background: rgba(74,222,128,0.12); border: 1px solid rgba(74,222,128,0.2); }
.wm-option-icon.import { background: rgba(96,165,250,0.12); border: 1px solid rgba(96,165,250,0.2); }
.wm-option-body { flex: 1; }
.wm-option-title { font-size: 14px; font-weight: 700; color: #fff; margin-bottom: 2px; }
.wm-option-desc  { font-size: 12px; color: rgba(255,255,255,0.4); }
.wm-option-arrow { color: rgba(255,255,255,0.25); font-size: 16px; flex-shrink: 0; }
.wm-option:hover .wm-option-arrow { color: #4ade80; }

/* Warning box */
.wm-warning {
  display: flex; gap: 12px; align-items: flex-start;
  background: rgba(240,192,96,0.08);
  border: 1px solid rgba(240,192,96,0.2);
  border-radius: 12px; padding: 14px 16px;
  margin-bottom: 20px;
  font-size: 12px; color: rgba(255,255,255,0.6);
  line-height: 1.6;
}
.wm-warning-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }

/* Mnemonic grid */
.mnemonic-grid {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 8px; margin-bottom: 20px;
}
.mnemonic-word {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px; padding: 8px 10px;
  display: flex; align-items: center; gap: 6px;
}
.mnemonic-num {
  font-family: 'DM Mono', monospace;
  font-size: 9px; color: rgba(255,255,255,0.25);
  flex-shrink: 0; min-width: 16px;
}
.mnemonic-text {
  font-family: 'DM Mono', monospace;
  font-size: 11px; color: #fff; font-weight: 500;
}

.mnemonic-actions {
  display: flex; gap: 10px; margin-bottom: 20px;
}

/* Checkbox confirm */
.wm-check-row {
  display: flex; align-items: flex-start; gap: 12px;
  margin-bottom: 20px; cursor: pointer;
}
.wm-check {
  width: 18px; height: 18px; flex-shrink: 0;
  border: 1.5px solid rgba(255,255,255,0.2);
  border-radius: 5px; margin-top: 1px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.wm-check.checked {
  background: #4ade80; border-color: #4ade80;
}
.wm-check-label { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.5; }

/* Import textarea */
.wm-textarea {
  width: 100%; background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px; padding: 14px 16px;
  font-family: 'DM Mono', monospace;
  font-size: 13px; color: #fff; resize: none; height: 110px;
  outline: none; line-height: 1.7;
  transition: border-color 0.2s;
  margin-bottom: 16px;
}
.wm-textarea:focus { border-color: rgba(74,222,128,0.3); }
.wm-textarea::placeholder { color: rgba(255,255,255,0.2); }

/* Address display */
.wm-addr-box {
  background: rgba(74,222,128,0.06);
  border: 1px solid rgba(74,222,128,0.2);
  border-radius: 12px; padding: 16px;
  margin-bottom: 20px;
}
.wm-addr-label {
  font-family: 'DM Mono', monospace;
  font-size: 9px; letter-spacing: 0.2em;
  color: rgba(74,222,128,0.6); text-transform: uppercase;
  margin-bottom: 6px;
}
.wm-addr-val {
  font-family: 'DM Mono', monospace;
  font-size: 12px; color: #4ade80;
  word-break: break-all; line-height: 1.5;
}

/* Buttons */
.wm-btn {
  width: 100%; padding: 14px;
  border-radius: 50px; font-family: 'Cabinet Grotesk', sans-serif;
  font-size: 14px; font-weight: 700; cursor: pointer;
  transition: all 0.2s; border: 1px solid transparent;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.wm-btn-primary {
  background: #4ade80; color: #000;
}
.wm-btn-primary:hover:not(:disabled) {
  background: #6ee79e;
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(74,222,128,0.25);
}
.wm-btn-outline {
  background: transparent; color: rgba(255,255,255,0.5);
  border-color: rgba(255,255,255,0.1);
}
.wm-btn-outline:hover { border-color: rgba(255,255,255,0.25); color: #fff; }
.wm-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.wm-divider {
  display: flex; align-items: center; gap: 12px;
  margin: 20px 0;
  font-family: 'DM Mono', monospace;
  font-size: 10px; color: rgba(255,255,255,0.2);
  letter-spacing: 0.1em;
}
.wm-divider::before, .wm-divider::after {
  content: ''; flex: 1; height: 1px;
  background: rgba(255,255,255,0.06);
}

.wm-error {
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.2);
  border-radius: 10px; padding: 12px 14px;
  font-size: 12px; color: #f87171;
  margin-top: 12px; line-height: 1.5;
}
.wm-success {
  background: rgba(74,222,128,0.08);
  border: 1px solid rgba(74,222,128,0.2);
  border-radius: 10px; padding: 12px 14px;
  font-size: 12px; color: #4ade80;
  margin-top: 12px; line-height: 1.5;
}

/* Connected banner */
.wm-connected-banner {
  background: rgba(74,222,128,0.08);
  border: 1px solid rgba(74,222,128,0.2);
  border-radius: 14px; padding: 20px;
  margin-bottom: 20px;
}
.wm-connected-type {
  font-family: 'DM Mono', monospace;
  font-size: 10px; letter-spacing: 0.15em;
  color: #4ade80; text-transform: uppercase; margin-bottom: 8px;
}
.wm-connected-bal {
  font-family: 'Cabinet Grotesk', sans-serif;
  font-size: 32px; font-weight: 900;
  color: #4ade80; line-height: 1; margin-bottom: 4px;
}
.wm-connected-usd {
  font-family: 'DM Mono', monospace;
  font-size: 12px; color: rgba(255,255,255,0.4);
}

.wm-spinner {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(0,0,0,0.2);
  border-top-color: #000; border-radius: 50%;
  animation: wm-spin 0.7s linear infinite;
}
.wm-spinner.light {
  border-color: rgba(255,255,255,0.1);
  border-top-color: #fff;
}
@keyframes wm-spin { to { transform: rotate(360deg); } }

.wm-keplr-info {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px; padding: 12px 14px;
  margin-bottom: 16px;
}
.wm-keplr-row {
  display: flex; justify-content: space-between;
  padding: 5px 0; font-size: 12px;
}
.wm-keplr-row:not(:last-child) { border-bottom: 1px solid rgba(255,255,255,0.06); padding-bottom: 8px; margin-bottom: 3px; }
.wm-keplr-label { font-family: 'DM Mono', monospace; font-size: 10px; color: rgba(255,255,255,0.3); }
.wm-keplr-val   { font-family: 'DM Mono', monospace; font-size: 11px; color: rgba(255,255,255,0.7); }
`;

// ── Inject styles once ────────────────────────────────────────────────────────
let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  const el = document.createElement('style');
  el.textContent = WALLET_STYLES;
  document.head.appendChild(el);
  stylesInjected = true;
}

// ── Logo component ────────────────────────────────────────────────────────────
function WmLogo() {
  return (
    <div className="wm-logo">
      <div className="wm-logo-mark">A</div>
      <div>
        <div className="wm-logo-text">AMITIS</div>
        <div className="wm-logo-sub">Sovereign Wallet</div>
      </div>
    </div>
  );
}

// ── MAIN MODAL ────────────────────────────────────────────────────────────────
export default function WalletModal({ open, onClose, nativeWallet, onKeplrConnect }) {
  injectStyles();

  const [view, setView]       = useState('main');
  const [copied, setCopied]   = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [importText, setImportText] = useState('');
  const [statusMsg, setStatusMsg]   = useState(null);
  const [statusOk, setStatusOk]     = useState(true);
  const [keplrLoading, setKeplrLoading] = useState(false);
  const [balance, setBalance] = useState(null);

  const { generate, importWallet, confirmBackup, disconnect, mnemonic, address, loading, error, isConnected } = nativeWallet;
  const keplr = useWallet();

  const isAnyConnected = isConnected || !!keplr.address;
  const connectedAddr  = address || keplr.address;
  const connectedType  = address ? 'native' : keplr.address ? 'keplr' : null;

  // Fetch balance when modal opens and wallet is connected
  React.useEffect(() => {
    if (open && connectedAddr) {
      const fetcher = isConnected ? nativeWallet.fetchBalance : keplr.fetchBalance || (() => Promise.resolve(0));
      fetcher(connectedAddr).then(b => setBalance(b));
    }
  }, [open, connectedAddr]);

  function handleClose() {
    setView('main');
    setImportText('');
    setStatusMsg(null);
    setConfirmed(false);
    onClose();
  }

  // ── Keplr connect ───────────────────────────────────────────────────────────
  async function handleKeplr() {
    setKeplrLoading(true);
    setStatusMsg(null);
    try {
      await keplr.connect();
      setStatusOk(true);
      setStatusMsg('✓ Keplr connected');
      setTimeout(handleClose, 800);
    } catch (e) {
      setStatusOk(false);
      setStatusMsg(e.message?.includes('rejected') ? 'Request rejected in Keplr.' : (e.message || 'Keplr connection failed.'));
    }
    setKeplrLoading(false);
  }

  // ── Generate wallet ─────────────────────────────────────────────────────────
  async function handleGenerate() {
    setStatusMsg(null);
    try {
      await generate();
      setView('mnemonic');
    } catch (e) {
      setStatusOk(false);
      setStatusMsg(e.message);
    }
  }

  // ── Confirm backup + proceed ────────────────────────────────────────────────
  function handleConfirmBackup() {
    confirmBackup();
    setView('connected');
  }

  // ── Copy mnemonic ───────────────────────────────────────────────────────────
  function copyMnemonic() {
    if (!mnemonic) return;
    navigator.clipboard.writeText(mnemonic).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // ── Import wallet ───────────────────────────────────────────────────────────
  async function handleImport() {
    setStatusMsg(null);
    try {
      const addr = await importWallet(importText);
      setStatusOk(true);
      setStatusMsg(`✓ Wallet imported — ${addr.slice(0,14)}…`);
      setTimeout(() => { setView('connected'); }, 800);
    } catch (e) {
      setStatusOk(false);
      setStatusMsg(e.message);
    }
  }

  // ── Disconnect ──────────────────────────────────────────────────────────────
  function handleDisconnect() {
    if (address) disconnect();
    if (keplr.address) keplr.disconnect?.();
    setBalance(null);
    setView('main');
    setStatusMsg(null);
  }

  const words = mnemonic ? mnemonic.split(' ') : [];
  const balFmt = balance !== null ? balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '—';
  const usdFmt = balance !== null ? `≈ $${(balance * 7.50).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  return (
    <div className={`wm-overlay ${open ? 'open' : ''}`} onClick={e => e.target.classList.contains('wm-overlay') && handleClose()}>
      <div className="wm-panel">
        <button className="wm-close" onClick={handleClose}>✕</button>

        {/* ── MAIN: choose connection method ── */}
        {view === 'main' && !isAnyConnected && (
          <>
            <WmLogo />
            <div className="wm-title">Connect your wallet</div>
            <div className="wm-sub">
              Choose how you'd like to connect to the Amitis Network.
              Your native wallet keys never leave your browser.
            </div>

            <button className="wm-option" onClick={handleKeplr} disabled={keplrLoading || loading}>
              <div className="wm-option-icon keplr">🔑</div>
              <div className="wm-option-body">
                <div className="wm-option-title">Keplr Wallet</div>
                <div className="wm-option-desc">Connect with your existing Keplr extension</div>
              </div>
              <div className="wm-option-arrow">{keplrLoading ? <div className="wm-spinner light"></div> : '→'}</div>
            </button>

            <div className="wm-divider">or use native wallet</div>

            <button className="wm-option" onClick={() => setView('generate')} disabled={loading}>
              <div className="wm-option-icon native">✦</div>
              <div className="wm-option-body">
                <div className="wm-option-title">Create New Wallet</div>
                <div className="wm-option-desc">Generate a new wallet directly in your browser</div>
              </div>
              <div className="wm-option-arrow">→</div>
            </button>

            <button className="wm-option" onClick={() => setView('import')} disabled={loading}>
              <div className="wm-option-icon import">↓</div>
              <div className="wm-option-body">
                <div className="wm-option-title">Import Wallet</div>
                <div className="wm-option-desc">Restore from a 24-word seed phrase</div>
              </div>
              <div className="wm-option-arrow">→</div>
            </button>

            {statusMsg && <div className={statusOk ? 'wm-success' : 'wm-error'}>{statusMsg}</div>}
          </>
        )}

        {/* ── ALREADY CONNECTED ── */}
        {(view === 'main' || view === 'connected') && isAnyConnected && (
          <>
            <WmLogo />
            <div className="wm-connected-banner">
              <div className="wm-connected-type">
                {connectedType === 'native' ? '✦ Native Wallet' : '🔑 Keplr Wallet'} · Connected
              </div>
              <div className="wm-connected-bal">{balFmt} AMTS</div>
              {usdFmt && <div className="wm-connected-usd">{usdFmt}</div>}
            </div>
            <div className="wm-addr-box">
              <div className="wm-addr-label">Your Address</div>
              <div className="wm-addr-val">{connectedAddr}</div>
            </div>
            <button
              className="wm-btn wm-btn-outline"
              style={{ marginBottom: 10 }}
              onClick={() => {
                navigator.clipboard.writeText(connectedAddr || '');
                setStatusMsg('✓ Address copied');
                setStatusOk(true);
                setTimeout(() => setStatusMsg(null), 1500);
              }}
            >
              Copy Address
            </button>
            <button className="wm-btn wm-btn-outline" onClick={handleDisconnect}>
              Disconnect
            </button>
            {statusMsg && <div className={statusOk ? 'wm-success' : 'wm-error'}>{statusMsg}</div>}
          </>
        )}

        {/* ── GENERATE: warning screen ── */}
        {view === 'generate' && (
          <>
            <button className="wm-back" onClick={() => setView('main')}>← Back</button>
            <WmLogo />
            <div className="wm-title">Create New Wallet</div>
            <div className="wm-sub">
              A 24-word seed phrase will be generated in your browser.
              Your keys never leave this device.
            </div>
            <div className="wm-warning">
              <span className="wm-warning-icon">⚠️</span>
              <span>
                Your seed phrase will be shown <strong style={{ color: '#f0c060' }}>once</strong>.
                Write it down and store it somewhere safe.
                Anyone who has your seed phrase can access your funds.
              </span>
            </div>
            <button className="wm-btn wm-btn-primary" onClick={handleGenerate} disabled={loading}>
              {loading ? <><div className="wm-spinner"></div> Generating…</> : 'Generate Wallet'}
            </button>
            {statusMsg && <div className={statusOk ? 'wm-success' : 'wm-error'}>{statusMsg}</div>}
          </>
        )}

        {/* ── MNEMONIC: show and confirm ── */}
        {view === 'mnemonic' && mnemonic && (
          <>
            <div className="wm-title">Save Your Seed Phrase</div>
            <div className="wm-sub">
              Write these 24 words down in order. Do not share them with anyone.
              This is the only way to recover your wallet.
            </div>

            <div className="mnemonic-grid">
              {words.map((w, i) => (
                <div className="mnemonic-word" key={i}>
                  <span className="mnemonic-num">{i + 1}</span>
                  <span className="mnemonic-text">{w}</span>
                </div>
              ))}
            </div>

            <div className="mnemonic-actions">
              <button
                className="wm-btn wm-btn-outline"
                style={{ flex: 1, padding: '10px' }}
                onClick={copyMnemonic}
              >
                {copied ? '✓ Copied' : 'Copy All Words'}
              </button>
            </div>

            <div className="wm-addr-box">
              <div className="wm-addr-label">Wallet Address</div>
              <div className="wm-addr-val">{address}</div>
            </div>

            <div
              className="wm-check-row"
              onClick={() => setConfirmed(c => !c)}
            >
              <div className={`wm-check ${confirmed ? 'checked' : ''}`}>
                {confirmed && <span style={{ color: '#000', fontSize: 11, fontWeight: 700 }}>✓</span>}
              </div>
              <div className="wm-check-label">
                I have written down my 24-word seed phrase and understand that losing it means permanently losing access to my wallet.
              </div>
            </div>

            <button
              className="wm-btn wm-btn-primary"
              disabled={!confirmed}
              onClick={handleConfirmBackup}
            >
              I've Saved My Seed Phrase →
            </button>
          </>
        )}

        {/* ── IMPORT: paste seed phrase ── */}
        {view === 'import' && (
          <>
            <button className="wm-back" onClick={() => setView('main')}>← Back</button>
            <WmLogo />
            <div className="wm-title">Import Wallet</div>
            <div className="wm-sub">
              Enter your 24-word seed phrase, separated by spaces.
              Your phrase is processed locally and never sent anywhere.
            </div>
            <div className="wm-warning">
              <span className="wm-warning-icon">🔒</span>
              <span>
                Only enter your seed phrase on a device you trust.
                Never share it with anyone — including support staff.
              </span>
            </div>
            <textarea
              className="wm-textarea"
              placeholder="word1 word2 word3 … word24"
              value={importText}
              onChange={e => setImportText(e.target.value)}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            <button
              className="wm-btn wm-btn-primary"
              disabled={loading || importText.trim().split(/\s+/).length < 12}
              onClick={handleImport}
            >
              {loading ? <><div className="wm-spinner"></div> Importing…</> : 'Import Wallet'}
            </button>
            {statusMsg && <div className={statusOk ? 'wm-success' : 'wm-error'}>{statusMsg}</div>}
          </>
        )}

      </div>
    </div>
  );
}
